import { z } from 'zod';
import { dateField } from '@shared/schemas';

// Schemas (source of truth)

/**
 * The same fields WITHOUT the `.default(false)` flags. `.partial()` does not
 * remove a default — an absent field still parses to `false` — so a patch built
 * from `configFloorSchema.partial()` silently writes every flag the caller left
 * out. That is fine for a create and wrong for an update, so the two shapes are
 * derived separately from this base.
 */
const configFloorFieldsSchema = z.object({
	projectId: z.string(),
	floorId: z.string(),
	floorNick: z.string(),
	shortFloorNick: z.string(),
	isSpecialFloor: z.boolean(),
	isMovingFloor: z.boolean(),
	isHoist: z.boolean(),
	isGroundFloor: z.boolean(),
	buildingId: z.string().optional(),
	matchingFixedFloorId: z.string().optional(),
	matchingFixedFloorNick: z.string().optional(),
	matchingFixedShortFloorNick: z.string().optional(),
	isMatchingFloorFixed: z.boolean().optional(),
	matchingFixedFloorChangeTimestamp: dateField.optional(),
});

export const configFloorSchema = configFloorFieldsSchema.extend({
	isSpecialFloor: z.boolean().default(false),
	isMovingFloor: z.boolean().default(false),
	isHoist: z.boolean().default(false),
	isGroundFloor: z.boolean().default(false),
});

/**
 * An update patch. `projectId` and `floorId` are absent by construction: both
 * are addressed by the URI, and the floor update cascades onto the floor's areas
 * and merged tags, so either one arriving in the body would rewrite those too.
 */
export const configFloorPatchSchema = configFloorFieldsSchema.omit({ projectId: true, floorId: true }).partial();

// Types (derived from schemas)
export type IConfigFloor = z.infer<typeof configFloorSchema>;
export type IConfigFloorPatch = z.infer<typeof configFloorPatchSchema>;
